import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from 'react-flexbox-grid';
import { FormattedMessage } from 'react-intl';
import { filter, path } from 'ramda';
import { Dialog, RaisedButton } from 'material-ui';

import HistoryCollapseContainer from 'containers/HistoryCollapseContainer';
import Paper from 'components/Paper';

import CommonHeader from 'routes/Documents/components/CommonHeader';
import BottomFormControls from 'routes/Documents/components/BottomFormControls';

import FSSForm from './FSSForm';
import MSISForm from './MSISForm';
import MSNQForm from './MSNQForm';
import FSMCForm from './FSMCForm';
import SDMTForm from './SDMTForm';

import FSSHistoryMapper, { sorter as fssSorter } from './FSSHistoryMapper';
import MSISHistoryMapper, { sorter as msisSorter } from './MSISHistoryMapper';
import SDMTHistoryMapper, { sorter as sdmtSorter } from './SDMTHistoryMapper';
import MSNQHistoryMapper, { sorter as msnqSorter } from './MSNQHistoryMapper';
import FSMCHistoryMapper, { sorter as fsmcSorter } from './FSMCHistoryMapper';
import calculateMSISTotals from 'routes/Documents/routes/Scores/calculateMSIS';
import { calculateFSSTotals } from 'routes/Documents/routes/Scores/calculateFSS';
import calculateMSNQTotals from 'routes/Documents/routes/Scores/calculateMSNQ';
import calculateFSMCTotals from 'routes/Documents/routes/Scores/calculateFSMC';
import { getScoreSummaryDialog } from 'routes/Documents/routes/Scores/getScoreSummaryDialog';

import { sectionHeader } from './Scores.less';
import normalize from 'lib/normalize';

import { getLatestFinishedCommitData, getLatestUnfinishedCommit, getLatestCommitDate } from 'lib/documentHandling';
import EditableV2 from 'routes/Documents/containers/EditableV2';

const headerText = {
  scores: ['scores.title', 'scores.infoText'],
  fss: ['fss.title'],
  msis: ['msis.title', 'msis.infoText'],
  sdmt: ['sdmt.title', 'sdmt.infoText'],
  msnq: ['msnq.title', 'msnq.infoText'],
  fsmc: ['fsmc.title', 'fsmc.infoText'],
};

class Scores extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      scoreDialogOpen: false,
      scoreDialogContent: undefined,
    };
  }

  setDialog = (doc, type) => () => {
    this.setState(getScoreSummaryDialog(doc, type, this.props.documents));
  };
  closeDialog = () => {
    this.setState({ scoreDialogOpen: false, scoreDialogContent: undefined });
  };
  dialogActions = [
    <RaisedButton label={<FormattedMessage id="infoDialog.close" />} primary onClick={this.closeDialog} />,
  ];

  componentDidUpdate(prevProps) {
    const prevEditDoc = prevProps.editingDocument;
    const editDocNow = prevEditDoc
      ? filter(d => d.documentId === prevEditDoc.documentId, this.props.documents)[0]
      : this.props.documents[0];

    // True if document was saved
    const saved =
      editDocNow &&
      editDocNow.commits.length > 0 &&
      getLatestCommitDate(editDocNow.commits) &&
      getLatestCommitDate(editDocNow.commits) !==
        ((prevEditDoc && prevEditDoc.commits.length > 0 && getLatestCommitDate(prevEditDoc.commits)) || 0);

    // If saved and no dialog open
    if (
      editDocNow &&
      !this.props.editingDocument &&
      prevProps.editingDocument &&
      saved &&
      !this.state.scoreDialogOpen
    ) {
      const docType = path(['documentType'], editDocNow);
      if (docType === 'msnq') {
        this.setDialog(normalize(calculateMSNQTotals([editDocNow]))[0], 'msnq')();
      } else if (docType === 'fsmc') {
        this.setDialog(calculateFSMCTotals([editDocNow])[0], 'fsmc')();
      }
    }
  }

  render() {
    const { documents, onStartEdit, onCreate, onView, viewId } = this.props;
    // Dont include status documents in editingDocuments
    const editingDocument =
      this.props.editingDocument && this.props.editingDocument.documentType === 'status'
        ? undefined
        : this.props.editingDocument;

    return (
      <div>
        <Paper topMargin={false}>
          <Grid fluid>
            <CommonHeader
              names={['msis', 'fss', 'msnq', 'fsmc']}
              localeId={
                editingDocument
                  ? headerText[editingDocument.documentType][0]
                  : viewId
                    ? headerText[viewId.name][0]
                    : headerText['scores'][0]
              }
              infoText={
                editingDocument
                  ? headerText[editingDocument.documentType]?.[1]
                  : viewId
                    ? headerText[viewId.name]?.[1]
                    : headerText['scores'][1]
              }
              mode="create"
              editing={editingDocument}
              startEdit={onCreate}
              infoElement={null}
              disableEditStartButton={!!viewId}
              descElement={
                !(editingDocument || viewId) ? (
                  <ul>
                    <li>
                      <FormattedMessage id="scores.description.MSIS-29" />
                    </li>
                    <li>
                      <FormattedMessage id="scores.description.FSS" />
                    </li>
                    <li>
                      <FormattedMessage id="scores.description.MSNQ" />
                    </li>
                    <li>
                      <FormattedMessage id="scores.description.FSMC" />
                    </li>
                  </ul>
                ) : (
                  undefined
                )
              }
            />
          </Grid>
          {editingDocument &&
            editingDocument.documentType === 'msis' && (
              <EditableV2
                documentId={editingDocument.documentId}
                moduleName={editingDocument.documentType}
                editedCommit={getLatestUnfinishedCommit(editingDocument)}
              >
                <MSISForm />
              </EditableV2>
            )}
          {editingDocument &&
            editingDocument.documentType === 'sdmt' && (
              <EditableV2
                documentId={editingDocument.documentId}
                moduleName={editingDocument.documentType}
                editedCommit={getLatestUnfinishedCommit(editingDocument)}
              >
                <SDMTForm editingDocument={editingDocument} />
              </EditableV2>
            )}
          {editingDocument &&
            editingDocument.documentType === 'fss' && (
              <EditableV2
                documentId={editingDocument.documentId}
                moduleName={editingDocument.documentType}
                editedCommit={getLatestUnfinishedCommit(editingDocument)}
              >
                <FSSForm />
              </EditableV2>
            )}
          {editingDocument &&
            editingDocument.documentType === 'msnq' && (
              <EditableV2
                documentId={editingDocument.documentId}
                moduleName={editingDocument.documentType}
                editedCommit={getLatestUnfinishedCommit(editingDocument)}
              >
                <MSNQForm />
              </EditableV2>
            )}
          {editingDocument &&
            editingDocument.documentType === 'fsmc' && (
              <EditableV2
                documentId={editingDocument.documentId}
                moduleName={editingDocument.documentType}
                editedCommit={getLatestUnfinishedCommit(editingDocument)}
              >
                <FSMCForm />
              </EditableV2>
            )}
          {!editingDocument &&
            viewId &&
            viewId.name === 'msis' && (
              <MSISForm
                editing={false}
                formValues={getLatestFinishedCommitData(documents.find(d => d.documentId === viewId.id))}
              />
            )}
          {!editingDocument &&
            viewId &&
            viewId.name === 'fss' && (
              <FSSForm
                editing={false}
                formValues={getLatestFinishedCommitData(documents.find(d => d.documentId === viewId.id))}
              />
            )}
          {!editingDocument &&
            viewId &&
            viewId.name === 'msnq' && (
              <MSNQForm
                editing={false}
                formValues={getLatestFinishedCommitData(documents.find(d => d.documentId === viewId.id))}
              />
            )}
          {!editingDocument &&
            viewId &&
            viewId.name === 'fsmc' && (
              <FSMCForm
                editing={false}
                formValues={getLatestFinishedCommitData(documents.find(d => d.documentId === viewId.id))}
              />
            )}
          {!editingDocument &&
            !viewId && (
              <div>
                {filter(d => d.documentType === 'sdmt', documents).length > 0 && (
                  <div>
                    <h4 className={sectionHeader}>
                      <FormattedMessage id="sdmt.title" />
                    </h4>
                    <HistoryCollapseContainer
                      documents={filter(d => d.documentType === 'sdmt', documents)}
                      emptyText="fss.emptyText"
                      sorter={sdmtSorter}
                      mapper={SDMTHistoryMapper('sdmt', onStartEdit, onView)}
                      limit={2}
                    />
                  </div>
                )}
                <div>
                  <h4 className={sectionHeader}>
                    <FormattedMessage id="msis.title" />
                  </h4>
                  <HistoryCollapseContainer
                    documents={calculateMSISTotals(filter(d => d.documentType === 'msis', documents))}
                    emptyText="msis.emptyText"
                    sorter={msisSorter}
                    mapper={MSISHistoryMapper('msis', onStartEdit, onView)}
                    limit={2}
                  />
                </div>
                <div>
                  <h4 className={sectionHeader}>
                    <FormattedMessage id="fss.title" />
                  </h4>
                  <HistoryCollapseContainer
                    documents={calculateFSSTotals(filter(d => d.documentType === 'fss', documents))}
                    emptyText="fss.emptyText"
                    sorter={fssSorter}
                    mapper={FSSHistoryMapper('fss', onStartEdit, onView)}
                    limit={2}
                  />
                </div>
                <div>
                  <h4 className={sectionHeader}>
                    <FormattedMessage id="msnq.title" />
                  </h4>
                  <HistoryCollapseContainer
                    documents={calculateMSNQTotals(filter(d => d.documentType === 'msnq', documents))}
                    emptyText="fss.emptyText"
                    sorter={msnqSorter}
                    mapper={MSNQHistoryMapper('msnq', onStartEdit, onView, this.setDialog)}
                    limit={2}
                  />
                </div>
                <div>
                  <h4 className={sectionHeader}>
                    <FormattedMessage id="fsmc.title" />
                  </h4>
                  <HistoryCollapseContainer
                    documents={calculateFSMCTotals(filter(d => d.documentType === 'fsmc', documents))}
                    emptyText="fsmc.emptyText"
                    sorter={fsmcSorter}
                    mapper={FSMCHistoryMapper('fsmc', onStartEdit, onView, this.setDialog)}
                    limit={2}
                  />
                </div>
              </div>
            )}
        </Paper>
        {editingDocument && (
          <BottomFormControls
            name={editingDocument.documentType}
            editId={editingDocument.documentId}
            commitId={getLatestUnfinishedCommit(editingDocument).commitId}
          />
        )}

        {
          <Dialog
            open={this.state.scoreDialogOpen}
            onRequestClose={this.closeDialog}
            actions={this.dialogActions}
            autoScrollBodyContent
          >
            {this.state.scoreDialogContent}
          </Dialog>
        }
      </div>
    );
  }
}

Scores.propTypes = {
  documents: PropTypes.array,
  onStartEdit: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  editingDocument: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  onView: PropTypes.func,
  viewId: PropTypes.object,
};

export default Scores;
